import { useQuery } from '@tanstack/react-query';

import type { ObtainMethod } from '@frontend/api';
import { useRequest, type QueryFunction } from '@frontend/api/src/common';

/**
 * Every reason a component is not allowed to become the parent of other components
 * during a BOM restructuring action. Mirrors `NewParentRestriction` in
 * `quotes/utils.py`.
 */
export type NewParentRestriction =
  | 'not_manufactured'
  | 'manual_unit_cost'
  | 'yield_below_100'
  | 'multiple_parents';

export type ComponentData = {
  id: number;
  partUuid: string;
  isRootComponent: boolean;
  filename: string;
  partNumber: null | string;
  revision: null | string;
  obtainMethod: ObtainMethod;
  isAssembly: boolean;
  childNodeIds: number[];
  innateQuantity: number;
  containsVirus: boolean;
  canBeNewParent: boolean;
  /** Empty when `canBeNewParent` is true. */
  newParentRestrictions: NewParentRestriction[];
  searchName: string;
};

type NodeData = {
  id: number;
  childUuid: string;
  count: number;
  position: number;
};

export type BOMRestructureInfo = {
  components: ComponentData[];
  nodes: NodeData[];
};

type UseGetBOMRestructureInfoParams = {
  quoteItemId: number;
};

export type GetBOMRestructureInfoQueryKeyType = readonly [
  'bomRestructureInfo',
  number,
];

export const getBOMRestructureInfoQueryKey = (
  params: UseGetBOMRestructureInfoParams,
): GetBOMRestructureInfoQueryKeyType => [
  'bomRestructureInfo',
  params.quoteItemId,
];

export const useGetBOMRestructureInfo: QueryFunction<
  BOMRestructureInfo,
  [UseGetBOMRestructureInfoParams],
  GetBOMRestructureInfoQueryKeyType
> = (params, { requestOptions, queryOptions } = {}) => {
  const request = useRequest(requestOptions);
  const queryKey = getBOMRestructureInfoQueryKey(params);

  return useQuery<
    BOMRestructureInfo,
    unknown,
    BOMRestructureInfo,
    GetBOMRestructureInfoQueryKeyType
  >({
    ...queryOptions,
    queryKey,
    queryFn: ({ signal }) => {
      return request(
        `/quotes/items/${params.quoteItemId}/bom_restructure_info/`,
        {
          init: {
            method: 'GET',
            signal,
          },
        },
      );
    },
  });
};
